// ============================================================
// FICHIER : Story.tsx (complet, corrigé)
// ============================================================

import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import { Page } from "../App";
import AirSharePanel from "../components/AirSharePanel";
import AirportAccess from "./AirportAccess";
import MobilityMarket from "./MobilityMarket";
import Map from "../components/Map";
import FooterPage from "../components/FooterPage";
import { AIRPORT_TYPES, COLORS } from "../config/carto";
import AirportSearch from "../components/AirportSearch";
import "./Story.css";
import MapLoadingOverlay from "../components/MapLoadingOverlay";
import IndiaMapHero from "../components/IndiaMapHero";

interface Props {
  navigate: (p: Page) => void;
}

const ATR_GRADIENT = "linear-gradient(135deg, #009A84 0%, #56C177 50%, #99D6EA 100%)";

const STEPS = [
  { id: "step-1", number: "01", title: "Access to airport", accent: "#009A84" },
  { id: "step-2", number: "02", title: "Why more airports needed", accent: "#009A84" },
  { id: "step-3", number: "03", title: "Expanding market access", accent: "#009A84" },
  { id: "step-4", number: "04", title: "New air demand", accent: "#009A84" },
  { id: "step-5", number: "05", title: "Where turboprops make the difference", accent: "#009A84" },
];

function hexToRgba(hex: string, alpha: number) {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function Section({ id, accent, children }: { id: string; accent: string; children: React.ReactNode }) {
  return (
    <section
      id={id}
      className="story-section"
      style={{ "--accent": accent, "--accent-glow": hexToRgba(accent, 0.12) } as React.CSSProperties}
    >
      {children}
    </section>
  );
}

/* ============================================================
   STEP 1 — The 90-minute limit
   ============================================================ */
function Step1() {
  const accent = STEPS[0].accent;
  return (
    <Section id="step-1" accent={accent}>
      {/* Styles pour l’animation et le survol */}
      <style>{`
        @keyframes cornerFadeIn {
          0% {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          100% {
            opacity: 0.7;
            transform: translateY(0) scale(1);
          }
        }
        .corner-deco {
          animation: cornerFadeIn 1.2s ease-out 0.3s both;
          transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1),
                      filter 0.4s ease,
                      opacity 0.4s ease;
          will-change: transform, filter, opacity;
        }
        .corner-deco:hover {
          transform: scale(1.08) rotate(-2deg);
          filter: drop-shadow(0 8px 24px rgba(0, 154, 132, 0.35));
          opacity: 1;
        }
      `}</style>

      {/* ── IMAGE DÉCORATIVE AVEC ANIMATION ── */}
      <img
        src="/photos/corner_design.png"
        alt=""
        className="corner-deco"
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          zIndex: 2,
          pointerEvents: "auto", /* permet le survol */
          width: "auto",
          height: "auto",
          maxWidth: "300px",
          maxHeight: "300px",
          opacity: 0.7,
          cursor: "default",
        }}
      />

      <div className="s1-row" style={{ position: "relative", zIndex: 100 }}>
        <div className="s1-text" style={{ flex: "0 0 520px" }}>
          <h1
            className="s1-title"
            style={{
              whiteSpace: 'nowrap',
              background: ATR_GRADIENT,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              filter: 'drop-shadow(0 0 20px rgba(0, 154, 132, 0.25))',
              fontSize: 'clamp(32px, 3.5vw, 48px)',
              marginBottom: '20px',
            }}
          >
            The 90-minute limit
          </h1>

          <div className="para-blur">
            <p className="s1-para" style={{ marginBottom: '16px' }}>
              Physical presence of an airport does not guarantee it gets used.
              Distance and travel time decide the choice — if the nearest airport
              is too far, travellers pick trains, buses, or private vehicles instead.
            </p>
            <p className="s1-para" style={{ marginBottom: '16px' }}>
              ATR's MobilityMonitor observed{' '}
              <span style={{ color: "white", fontWeight: 700 }}>millions of real journeys</span>
              {' '}to measure exactly how the probability of flying changes with drive time to the
              closest airport.
            </p>
            <p className="s1-para" style={{ marginBottom: '16px' }}>
              Within <span style={{ color: "white", fontWeight: 700 }}>90 minutes</span>,
              air demand can be up to{' '}
              <span style={{ color: "white", fontWeight: 700 }}>3× higher</span>{' '}
              than beyond that range.
            </p>
            <p className="s1-para" style={{ marginBottom: '8px' }}>
              Today, only about{' '}
              <span style={{ color: "white", fontWeight: 700 }}>30% of India's population</span>{' '}
              lives within that reach.
            </p>
          </div>
        </div>
        <div className="s1-visual" style={{ position: "relative", zIndex: 100 }}>
          <AirSharePanel />
        </div>
      </div>
    </Section>
  );
}

/* ============================================================
   STEP 2 — Why more airports are needed
   ============================================================ */
function Step2({ navigate }: { navigate: (p: Page) => void }) {
  const accent = STEPS[1].accent;
  const [fullMapOpen, setFullMapOpen] = useState(false);
  const [previewTime, setPreviewTime] = useState(90);
  const [previewCount, setPreviewCount] = useState(0);
  const [previewTotalPop, setPreviewTotalPop] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [airportStatus, setAirportStatus] = useState<string[]>(["Scheduled operations"]);
  const TIME_OPTIONS = [30, 60, 90, 120, 150, 180];

  // Couleurs ATR pour les statuts
  const STATUS_COLORS: Record<string, string> = {
    "Scheduled operations": "#009A84",
    "Potential location": "#56C177",
    "Unscheduled operations": "#99D6EA",
    "Under construction": "#EB280F", 
    "Construction plan": "#00313C",  
  };

  if (fullMapOpen) {
    return (
      <div className="story-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setFullMapOpen(false); }}>
        <div className="story-modal-window">
          <AirportAccess navigate={navigate} />
          <button className="story-modal-close" onClick={() => setFullMapOpen(false)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return (
    <Section id="step-2" accent={accent}>
      {/* Carte */}
      <div className="story-map-fullbleed">
        <Map
          time={previewTime}
          airportStatus={airportStatus}
          showPop={false}
          setCount={setPreviewCount}
          setTotalPop={setPreviewTotalPop}
          setLoading={setPreviewLoading}
          setError={setPreviewError}
          initialZoom={5.4}
          scrollWheelZoom={false}
        />
        <MapLoadingOverlay loading={previewLoading} label={`${previewTime} min`} />
      </div>

      <div className="story-overlay-gradient" />

      {/* ── PANNEAU TEXTE ── */}
      <div className="story-overlay-panel" style={{ width: '1000px' }}>
        <h1
          className="s2-title"
          style={{
            whiteSpace: 'nowrap',
            overflow: 'visible',
            background: 'linear-gradient(135deg, #009A84 0%, #56C177 50%, #99D6EA 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            filter: 'drop-shadow(0 0 20px rgba(0, 154, 132, 0.25))',
            fontSize: 'clamp(30px, 3.2vw, 42px)',
            marginBottom: '20px',
            letterSpacing: '-0.5px',
            width: '100%',
            maxWidth: '100%',
          }}
        >
          Why more airports are needed?
        </h1>

        {/* Groupe de paragraphes avec un seul flou */}
        <div className="para-blur" style={{ maxWidth: '520px' }}>
          <p className="s2-para" style={{ marginBottom: '14px' }}>
            India targets <strong style={{ color: 'white' }}>230 airports by 2030</strong>.
            MobilityMonitor modelled a <strong style={{ color: 'white' }}>200-airport</strong> scenario,
            identifying <strong style={{ color: 'white' }}>39</strong> airports under construction,
            <strong style={{ color: 'white' }}>7</strong> with advanced plans,
            and <strong style={{ color: 'white' }}>38</strong> developable airfields.
          </p>

          <p className="s2-para" style={{ marginBottom: '14px' }}>
            In total, this represents <strong style={{ color: 'white' }}>+85 additional airports</strong> —
            a significant expansion of India's aviation infrastructure.
          </p>

          <p className="s2-para" style={{ marginBottom: '8px' }}>
            Today, only <strong style={{ color: 'white' }}>425 million</strong> people live within
            90 minutes of an airport.
          </p>

          <p className="s2-para" style={{ marginBottom: '8px' }}>
            Adding these <strong style={{ color: 'white' }}>85 airports</strong> would extend access to
            <strong style={{ color: 'white' }}>175 million</strong> more people —
            bringing the total to <strong style={{ color: 'white' }}>600 million</strong>.
          </p>
        </div>
      </div>

      {/* ── STATS ── */}
      <div className="story-stats-vertical">
        <div className="story-stat-card">
          <div className="story-stat-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="#009A84" strokeWidth="1.8">
              <path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 00-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" />
            </svg>
          </div>
          <div className="story-stat-content">
            <span className="story-stat-value">
              <span className="accent-turquoise">
                {previewLoading ? "—" : previewCount}
              </span>
            </span>
            <span className="story-stat-label">Airports</span>
            <span className="story-stat-sub">in selected area</span>
          </div>
        </div>

        <div className="story-stat-card">
          <div className="story-stat-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="#56C177" strokeWidth="1.8">
              <circle cx="12" cy="12" r="10" />
              <path d="M2 12h20M12 2a15 15 0 010 20M12 2a15 15 0 000 20" />
            </svg>
          </div>
          <div className="story-stat-content">
            <span className="story-stat-value">
              <span className="accent-green">
                {previewLoading ? "—" : previewTotalPop > 0 ? (previewTotalPop / 1_000_000).toFixed(1) + "M" : "—"}
              </span>
            </span>
            <span className="story-stat-label">Population reached</span>
            <span className="story-stat-sub">within {previewTime} min</span>
          </div>
        </div>
      </div>

      {/* ── LÉGENDE ── */}
      <div className="story-legend-wrapper">
        <div className="story-legend">
          <div className="story-legend-header">
              <span className="story-legend-label" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {/* Icône avion depuis /photos avec filtre ATR + rotation vers le haut */}
                <img
                  aria-hidden="true"
                  style={{
                    display: "inline-block",
                    width: 18,
                    height: 18,
                    background: "linear-gradient(135deg, #00E5C4 0%, #009A84 50%, #045A4C 100%)",
                    WebkitMaskImage: "url(/photos/ATR-plane-icon.svg)",
                    maskImage: "url(/photos/ATR-plane-icon.svg)",
                    WebkitMaskRepeat: "no-repeat",
                    maskRepeat: "no-repeat",
                    WebkitMaskSize: "contain",
                    maskSize: "contain",
                    WebkitMaskPosition: "center",
                    maskPosition: "center",
                    transform: "rotate(135deg)", // 90° + un peu plus dans le sens horaire
                    flexShrink: 0,
                  }}
                />
                Select airport status
              </span>
          </div>
          {AIRPORT_TYPES.map((s) => {
            const isChecked = airportStatus.includes(s);
            const color = STATUS_COLORS[s] || "#fff";
            return (
              <div
                key={s}
                className={`story-legend-item ${isChecked ? "active" : ""}`}
                onClick={() => {
                  if (isChecked) setAirportStatus(airportStatus.filter((x) => x !== s));
                  else setAirportStatus([...airportStatus, s]);
                }}
              >
                <div className="story-legend-dot" style={{
                  width: "12px", height: "12px", minWidth: "12px", minHeight: "12px",
                  borderRadius: "50%",
                  background: isChecked ? color : "rgba(255,255,255,0.1)",
                  border: `2px solid ${isChecked ? color : "rgba(255,255,255,0.15)"}`,
                  boxShadow: isChecked ? `0 0 12px ${color}60` : "none",
                  flexShrink: 0, transition: "all 0.2s ease",
                }} />
                <span>{s}</span>
                <div className={`story-legend-check ${isChecked ? "checked" : ""}`}
                  style={{ "--check-color": isChecked ? color : "transparent", "--check-glow": isChecked ? `${color}50` : "transparent" } as React.CSSProperties}>
                  {isChecked && <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5"><path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                </div>
              </div>
            );
          })}
          <div className="story-legend-count"><span>{airportStatus.length}</span> / {AIRPORT_TYPES.length} active</div>
        </div>
      </div>

      {/* ── TIME PILLS ── */}
      <div className="story-time-wrapper">
        <div className="story-map-pills">
          <span className="story-pills-label">
            {/* Icône horloge */}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#009A84" strokeWidth="2" style={{ marginRight: 6 }}>
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
            Select drive time
          </span>
          {TIME_OPTIONS.map((t) => (
            <button key={t} className={`story-map-pill ${previewTime === t ? "active" : ""}`} onClick={() => setPreviewTime(t)}>
              {t} min
            </button>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ============================================================
   STEP 3 — Expanding market access (avec recherche)
   ============================================================ */
function Step3() {
  const accent = STEPS[2].accent;
  const [mapLoading, setMapLoading] = useState(true);
  const [mobilityFullscreen, setMobilityFullscreen] = useState(false);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  const [selectedMetro, setSelectedMetro] = useState<any>(null);
  const [mapInstance, setMapInstance] = useState<any>(null);

  useEffect(() => {
    const t = setTimeout(() => setMapLoading(false), 2800);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.3 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const zoomToMetro = (metro: any) => {
    setSelectedMetro(metro);
    if (mapInstance) {
      // Utiliser les coordonnées du métro pour zoomer
      mapInstance.zoomToMetro(metro);
    }
  };

  const clearMetro = () => {
    setSelectedMetro(null);
    if (mapInstance) {
      mapInstance.resetView();
    }
  };

  if (mobilityFullscreen) {
    return (
      <div className="story-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setMobilityFullscreen(false); }}>
        <div className="story-modal-window">
          <MobilityMarket navigate={() => {}} />
          <MapLoadingOverlay loading={mapLoading} label="mobility data" />
          <button className="story-modal-close" onClick={() => setMobilityFullscreen(false)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return (
    <Section id="step-3" accent={accent}>
      <div className="story-map-fullbleed" ref={ref as React.RefObject<HTMLDivElement>}>
        <MobilityMarket 
          navigate={() => {}} 
          onMapReady={setMapInstance}
          selectedMetro={selectedMetro}
        />
        <div style={{ position: "absolute", inset: 0, zIndex: 1, pointerEvents: "none" }}
          ref={(el) => {
            if (!el) return;
            el.addEventListener("wheel", (e: WheelEvent) => {
              e.stopImmediatePropagation();
              const scrollEl = el.closest(".story-scroll");
              if (scrollEl) scrollEl.scrollBy({ top: e.deltaY, behavior: "auto" });
            }, { capture: true });
          }} />
      </div>

      <div className="story-overlay-gradient" />

      {/* ── PANNEAU TEXTE ── */}
      <div className="story-overlay-panel" style={{ width: '480px' }}>
        <h1 
          className="s3-title" 
          style={{
            whiteSpace: 'nowrap',
            overflow: 'visible',
            background: 'linear-gradient(135deg, #009A84 0%, #56C177 50%, #99D6EA 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            filter: 'drop-shadow(0 0 20px rgba(0, 154, 132, 0.25))',
            fontSize: 'clamp(30px, 3.2vw, 42px)',
            marginBottom: '20px',
            letterSpacing: '-0.5px',
            width: '120%',
            height: '50px',
            maxWidth: '120%',
            paddingRight: '20px',
          }}
        >
          Expanding market access
        </h1>

        <div className="para-blur" style={{ maxWidth: '440px' }}>
          <p className="s3-para" style={{ marginBottom: '16px' }}>
            India's total inter‑city mobility market is around{' '}
            <strong style={{ color: 'white' }}>4.6 billion journeys per year</strong>.
          </p>
          <p className="s3-para" style={{ marginBottom: '16px' }}>
            The catchment areas of today's <strong style={{ color: 'white' }}>120</strong> mainland airports
            cover <strong style={{ color: 'white' }}>2.5 billion</strong> of these journeys.
          </p>
          <p className="s3-para" style={{ marginBottom: '16px' }}>
            The <strong style={{ color: 'white' }}>85 new airports</strong> unlock a further{' '}
            <strong style={{ color: 'white' }}>1.25 billion</strong> inter‑city journeys per year,
            expanding the accessible market by <strong style={{ color: 'white' }}>+50%</strong>.
          </p>
        </div>
      </div>

      {/* ── Key figures + Recherche ── */}
      <div className="s3-bottom-stats">
        <div className="s3-key-figures">
          <div className="s3-key-figures-title">Key figures</div>
          {[
            { val: "4.6B", label: "Total inter-city journeys/year", color: "#009A84" },
            { val: "2.5B", label: "Within reach of current airports", color: "#56C177" },
            { val: "+1.25B", label: "Unlocked by 85 new airports", color: "#99D6EA" },
            { val: "+50%", label: "Market expansion", color: "#00313C" },
          ].map((s, i) => (
            <div key={s.label} className="s3-key-item" style={{
              opacity: visible ? 1 : 0,
              transform: visible ? "translateX(0)" : "translateX(12px)",
              transition: `opacity 0.6s ease ${i * 0.12}s, transform 0.6s cubic-bezier(0.16,1,0.3,1) ${i * 0.12}s`,
            }}>
              <span className="s3-key-value" style={{ color: s.color }}>{s.val}</span>
              <span className="s3-key-label">{s.label}</span>
            </div>
          ))}
        </div>

        <div className="s3-search-wrapper">
          <div className="s3-search-header">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <span>Find a city or metro area</span>
          </div>
          <AirportSearch 
            onSelect={zoomToMetro}
            onClear={clearMetro}
            mapInstance={mapInstance}
          />
          {selectedMetro && (
            <div className="s3-selected-airport">
              <span className="s3-selected-name">{selectedMetro.metro_origin}</span>
              <span className="s3-selected-code" style={{ color: '#009A84' }}>
                {selectedMetro.lat.toFixed(2)}, {selectedMetro.lng.toFixed(2)}
              </span>
            </div>
          )}
        </div>
      </div>
    </Section>
  );
}

/* ============================================================
   STEP 4 — New air demand
   ============================================================ */

interface CountUpProps {
  target: number;
  delay?: number;
  duration?: number;
  suffix?: string;
  trigger: boolean;
  formatter?: (value: number) => string;
}

function CountUp({ target, delay = 0, duration = 900, suffix = "", trigger, formatter }: CountUpProps) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!trigger) return;
    let rafId: number;
    const startTimeout = setTimeout(() => {
      const startTime = performance.now();
      const step = (now: number) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - (1 - progress) * (1 - progress);
        setValue(Math.round(eased * target));
        if (progress < 1) rafId = requestAnimationFrame(step);
      };
      rafId = requestAnimationFrame(step);
    }, delay * 1000);

    return () => {
      clearTimeout(startTimeout);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [trigger, target, delay, duration]);

  if (formatter) return <>{formatter(value)}</>;
  return <>{value}{suffix}</>;
}

function FlyingPlanesBackground() {
  const planes = [
    { top: "12%", duration: 22, delay: 0,  scale: 0.7,  opacity: 0.16 },
    { top: "28%", duration: 30, delay: 6,  scale: 0.5,  opacity: 0.12 },
    { top: "48%", duration: 26, delay: 3,  scale: 0.9,  opacity: 0.14 },
    { top: "65%", duration: 34, delay: 12, scale: 0.55, opacity: 0.10 },
    { top: "82%", duration: 24, delay: 9,  scale: 0.75, opacity: 0.13 },
    { top: "38%", duration: 28, delay: 15, scale: 0.6,  opacity: 0.1  },
    { top: "70%", duration: 20, delay: 4,  scale: 0.65, opacity: 0.15 },
    { top: "6%",  duration: 28, delay: 15, scale: 0.6,  opacity: 0.1  },
    { top: "90%", duration: 20, delay: 4,  scale: 0.65, opacity: 0.15 },
  ];

  return (
    <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
      <style>{`
        @keyframes flyAcross {
          from { transform: translateX(-10%); }
          to { transform: translateX(110vw); }
        }
      `}</style>
      {planes.map((p, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            top: p.top,
            left: 0,
            width: 48 * p.scale,
            height: 48 * p.scale,
            animation: `flyAcross ${p.duration}s linear ${p.delay}s infinite`,
          }}
        >
          <img
            src="/photos/ATR-plane-icon.svg"
            alt=""
            style={{
              width: "100%",
              height: "100%",
              opacity: p.opacity,
              transform: "rotate(-90deg)",
              filter: "brightness(0) invert(1)",
            }}
          />
        </div>
      ))}
    </div>
  );
}

function Step4() {
  const accent = STEPS[3].accent;
  const passengerTiers = [
    { t1: "Tier 1", t2: "Tier 1", curReg: 11283712, futReg: 11401336 },
    { t1: "Tier 1", t2: "Tier 2", curReg: 11593065, futReg: 17064817 },
    { t1: "Tier 1", t2: "Tier 3", curReg: 29201023, futReg: 39902987 },
    { t1: "Tier 2", t2: "Tier 2", curReg: 902242,   futReg: 8478746  },
    { t1: "Tier 2", t2: "Tier 3", curReg: 2631597,  futReg: 10164109 },
    { t1: "Tier 3", t2: "Tier 3", curReg: 3798452,  futReg: 5328760  },
  ];

  const toM = (v: number) => v / 1_000_000;
  const totalGrowth = passengerTiers.reduce((sum, t) => sum + (t.futReg - t.curReg), 0);
  const pctOf = (t: typeof passengerTiers[0]) => ((t.futReg - t.curReg) / totalGrowth) * 100;

  const sequence = [1, 2, 3, 4, 5];
  const mergedPct = Math.round(sequence.reduce((sum, i) => sum + pctOf(passengerTiers[i]), 0));

  const maxValue = 40;
  const gridValues = ["40M", "30M", "20M", "10M", "0M"];
  const CHART_HEIGHT = 280;
  const WHITE = "rgba(255,255,255,0.25)";
  const TEAL = "#009983";
  const YELLOW = "#56C177";

  const [animate, setAnimate] = useState(false);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAnimate(true);
          observer.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Section id="step-4" accent={accent}>
      <style>{`
        @keyframes arrowBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.35; }
        }
        .tier-arrow {
          animation: arrowBlink 0.8s ease-in-out infinite;
        }
      `}</style>


     <div style={{ position: "relative", zIndex: 1, maxWidth: "none", width: "90%", marginTop: 24 }}>
  <h1
    className="s1-title"
    style={{
      fontSize: "clamp(30px, 3.6vw, 48px)",
      background: "linear-gradient(90deg, #009A84 0%, #56C177 50%, #99D6EA 100%)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
      color: "transparent",
      display: "inline-block",
      marginLeft: 0,
      paddingLeft: 0,
    }}
  >
    Converting mobility into<br />
    air passengers
  </h1>
  <div className="s1-row" ref={sectionRef} style={{ maxWidth: "none", width: "100%" }}>
    {/* LEFT */}
    <div className="s1-text" style={{ marginLeft: -40, paddingLeft: 0 }}>

            <p className="s1-para">
              MobilityMonitor identifies over <strong style={{ color: "white" }}>900 new city-pairs</strong> with
              real potential demand — expanding the network to more than{" "}
              <strong style={{ color: "white" }}>1,500 routes</strong>.
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: 10, margin: "16px 0" }}>
              {[
                "None connect two Tier 1 cities.",
                <>30% connecting <strong style={{ color: "white" }}>Tier 2</strong> and <strong style={{ color: "white" }}>Tier 3</strong> cities</>,
                <>62% involving <strong style={{ color: "white" }}>only</strong> <strong style={{ color: "white" }}>Tier 2</strong> or <strong style={{ color: "white" }}>Tier 3</strong> cities</>,
              ].map((content, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    gap: 10,
                    fontSize: 14,
                    lineHeight: 1.6,
                    color: "rgba(255,255,255,0.7)",
                    fontWeight: 300,
                  }}
                >
                  <span style={{ color: YELLOW, fontWeight: 700, flexShrink: 0 }}>→</span>
                  <span>{content}</span>
                </div>
              ))}
            </div>

            <p className="s1-para">
              Domestic passenger volumes grow from <strong style={{ color: "white" }}>150M to 240M/year</strong>.
            </p>
            <p className="s1-para">
              Of the ~90M new passengers, <strong style={{ color: "white" }}>~35M (39%)</strong> travel on{" "}
              <strong style={{ color: "white" }}>regional routes</strong>.
            </p>

            <p className="s1-para" style={{ fontWeight: 700, color: "white" }}>
              {mergedPct}% of regional passenger growth concerns Tier 2 or Tier 3 cities. Explore the chart to see how
              each tier pair contributes.
            </p>
          </div>

          {/* RIGHT */}
<div className="s1-visual" style={{ position: "relative", display: "flex", flexDirection: "column", marginRight: "-60px", }}>
  <div style={{ marginBottom: 14, marginLeft: -34 }}>
    <div
      style={{
        fontSize: "clamp(10px, 1vw, 13px)",
        letterSpacing: 1,
        color: "rgba(255,255,255,0.8)",
        textTransform: "uppercase",
        marginBottom: 6,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      Regional passenger demand by city tier flow
    </div>
    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", marginBottom: 10 }}>
      Annual regional passengers
    </div>
    <div
      style={{
        display: "flex",
        gap: 16,
        fontSize: 12.5,
        color: "rgba(255,255,255,0.7)",
        flexWrap: "wrap",
        marginBottom: 6,
      }}
    >
      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ width: 9, height: 9, borderRadius: 2, background: "rgba(255,255,255,0.25)" }} /> Current
        regional network
      </span>
      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ width: 9, height: 9, borderRadius: 2, background: TEAL }} /> Future additional regional
        network
      </span>
    </div>
  </div>

  <div
    style={{
      position: "relative",
      display: "flex",
      alignItems: "flex-end",
      gap: 20,
      height: CHART_HEIGHT,
      marginTop: 24,
      width: "100%",
      boxSizing: "border-box",
      paddingLeft: 34,
    }}
  >
    <div
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        pointerEvents: "none",
        zIndex: 0,
      }}
    >
      {gridValues.map((value) => (
        <div
          key={value}
          style={{
            width: "100%",
            borderTop: `1px ${value === "0M" ? "solid" : "dashed"} rgba(255,255,255,0.15)`,
            position: "relative",
          }}
        >
          <span
            style={{
              position: "absolute",
              left: -30,
              top: -6,
              color: "rgba(255,255,255,0.55)",
              fontSize: 9.5,
            }}
          >
            {value}
          </span>
        </div>
      ))}
    </div>

    {passengerTiers.map((t, i) => {
      const curHeightPct = (toM(t.curReg) / maxValue) * 100;
      const futHeightPct = (toM(t.futReg) / maxValue) * 100;
      return (
        <div
          key={`${t.t1}-${t.t2}`}
          style={{
            flex: 1,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            alignItems: "center",
            position: "relative",
            zIndex: 1,
          }}
          onMouseEnter={() => setHoverIndex(i)}
          onMouseLeave={() => setHoverIndex(null)}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              gap: 4,
              height: "100%",
              width: "72%",
            }}
          >
            <div
              style={{
                flex: 1,
                height: animate ? `${curHeightPct}%` : "0%",
                borderRadius: "3px 3px 0 0",
                background: WHITE,
                transition: `height 1s ease ${i * 0.1}s`,
                boxShadow: hoverIndex === i ? "0 0 0 2px rgba(255,255,255,0.4)" : "none",
              }}
            />
            <div
              style={{
                flex: 1,
                height: animate ? `${futHeightPct}%` : "0%",
                borderRadius: "3px 3px 0 0",
                background: TEAL,
                transition: `height 1s ease ${i * 0.1}s`,
                boxShadow: hoverIndex === i ? "0 0 0 2px rgba(255,255,255,0.4)" : "none",
              }}
            />
          </div>

          <div
            style={{
              position: "absolute",
              top: "100%",
              left: "50%",
              transform: "translateX(-50%)",
              marginTop: 6,
              fontSize: 10,
              lineHeight: 1.3,
              color: YELLOW,
              textAlign: "center",
              whiteSpace: "nowrap",
            }}
          >
            {t.t1}<br />
            <span className="tier-arrow">↔</span> {t.t2}
          </div>

          {hoverIndex === i && (
            <div
              style={{
                position: "absolute",
                bottom: `calc(${Math.max(curHeightPct, futHeightPct)}% + 26px)`,
                left: "50%",
                transform: "translateX(-50%)",
                background: "#0d3a3a",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: 8,
                padding: "10px 14px",
                fontSize: 10.5,
                color: "rgba(255,255,255,0.85)",
                whiteSpace: "nowrap",
                zIndex: 5,
                boxShadow: "0 4px 14px rgba(0,0,0,0.4)",
                textAlign: "left",
              }}
            >
              <div>
                Current regional: <strong style={{ color: "white" }}>{Math.round(toM(t.curReg))}M</strong>
              </div>
              <div>
                Future regional: <strong style={{ color: "white" }}>{Math.round(toM(t.futReg))}M</strong>
              </div>
              <div>
                New regional passengers:{" "}
                <strong style={{ color: TEAL }}>
                  +{Math.round(toM(t.futReg)) - Math.round(toM(t.curReg))}M
                </strong>
              </div>
              <div>{Math.round(pctOf(t))}% of regional growth</div>
            </div>
          )}
        </div>
      );
    })}
  </div>
</div>
        </div>
      </div>
    </Section>
  );
}

/* ============================================================
   STEP 5 — Where turboprops make the difference
   ============================================================ */
function Turntable() {
  const FRAME_COUNT = 9;
  const containerRef = useRef<HTMLDivElement>(null);
  const [frame, setFrame] = useState(1); // 1 à 9
  const [dragging, setDragging] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const draggingRef = useRef(false);
  const lastXRef = useRef(0);
  const accumRef = useRef(0);

  const PIXELS_PER_FRAME = 12;

  const goNext = () => setFrame((prev) => (prev % FRAME_COUNT) + 1);
  const goPrev = () => setFrame((prev) => ((prev - 2 + FRAME_COUNT) % FRAME_COUNT) + 1);

  const handlePointerDown = (e: React.PointerEvent) => {
    draggingRef.current = true;
    setDragging(true);
    lastXRef.current = e.clientX;
    accumRef.current = 0;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current) return;
    const deltaX = e.clientX - lastXRef.current;
    lastXRef.current = e.clientX;
    accumRef.current += deltaX;

    if (Math.abs(accumRef.current) >= PIXELS_PER_FRAME) {
      const steps = Math.trunc(accumRef.current / PIXELS_PER_FRAME);
      accumRef.current -= steps * PIXELS_PER_FRAME;
      setFrame((prev) => {
        let next = ((prev - 1 + steps) % FRAME_COUNT + FRAME_COUNT) % FRAME_COUNT;
        return next + 1;
      });
    }
  };

  const handlePointerUp = () => {
    draggingRef.current = false;
    setDragging(false);
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    const handleChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handleChange);
    return () => document.removeEventListener("fullscreenchange", handleChange);
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
        height: isFullscreen ? "100vh" : 320,
        borderRadius: isFullscreen ? 0 : 16,
        overflow: "hidden",
        background: isFullscreen ? "#07282f" : "rgba(255,255,255,0.03)",
      }}
    >
      <div
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          cursor: dragging ? "grabbing" : "grab",
          touchAction: "none",
          userSelect: "none",
        }}
      >
        {Array.from({ length: FRAME_COUNT }, (_, i) => i + 1).map((n) => (
          <img
            key={n}
            src={`/photos/ATR 3D picture transp/${n}.png`}
            alt={``}
            draggable={false}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              opacity: n === frame ? 1 : 0,
              pointerEvents: "none",
              transition: dragging ? "none" : "opacity 0.15s ease",
            }}
          />
        ))}
      </div>

      {/* Flèche gauche */}
      <button
        onClick={goPrev}
        style={{
          position: "absolute",
          bottom: 12,
          left: "50%",
          transform: "translateX(calc(-50% - 50px))",
          width: 32,
          height: 32,
          borderRadius: "50%",
          border: "1px solid rgba(255,255,255,0.2)",
          background: "rgba(0,0,0,0.4)",
          color: "white",
          fontSize: 14,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 5,
        }}
      >
        ←
      </button>

      {/* Flèche droite */}
      <button
        onClick={goNext}
        style={{
          position: "absolute",
          bottom: 12,
          left: "50%",
          transform: "translateX(calc(-50% + 50px))",
          width: 32,
          height: 32,
          borderRadius: "50%",
          border: "1px solid rgba(255,255,255,0.2)",
          background: "rgba(0,0,0,0.4)",
          color: "white",
          fontSize: 14,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 5,
        }}
      >
        →
      </button>

      {/* Bouton plein écran */}
      <button
        onClick={toggleFullscreen}
        style={{
          position: "absolute",
          top: 12,
          right: 12,
          width: 32,
          height: 32,
          borderRadius: 8,
          border: "1px solid rgba(255,255,255,0.2)",
          background: "rgba(0,0,0,0.4)",
          color: "white",
          fontSize: 14,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 5,
        }}
      >
        {isFullscreen ? "⤢" : "⛶"}
      </button>

      {/* Indicateur de position */}
      <span
        style={{
          position: "absolute",
          bottom: 12,
          left: "50%",
          transform: "translateX(-50%)",
          fontSize: 11,
          color: "rgba(255,255,255,0.5)",
          letterSpacing: 1,
          pointerEvents: "none",
          zIndex: 5,
        }}
      >
        {frame} / {FRAME_COUNT}
      </span>
    </div>
  );
}

function Step5() {
  const accent = STEPS[4].accent;

  return (
    <Section id="step-5" accent={accent}>
      <style>{`
        .s5-row {
          max-width: none !important;
          width: 100% !important;
        }
      `}</style>

      <div style={{ position: "relative", zIndex: 1 }}>
       <h1
  className="s5-title"
  style={{
    whiteSpace: "nowrap",
    fontSize: "clamp(30px, 3.6vw, 48px)",
    background: "linear-gradient(135deg, #009A84 0%, #56C177 50%, #99D6EA 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    color: "transparent",
    display: "inline-block",
  }}
>
  Where turboprops make the difference
</h1>
        <div
  className="s1-row"
  style={{
    marginTop: 24,
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.3fr)", // texte | carte, la carte a plus de place
    gap: 32,
    alignItems: "stretch",
    width: "100%",
  }}
>
  <div className="s1-text" style={{ display: "flex", flexDirection: "column", minWidth: 0,  marginLeft: -34,}}>
         
            <p className="s1-para">
              Regional routes — under <strong style={{ color: "white" }}>740 km</strong> —
              make up <strong style={{ color: "white" }}>90% of India's inter-city journeys</strong>,
              exactly where turboprop economics shine.
            </p>
            <p className="s1-para" style={{ marginBottom: 12 }}>
              <strong style={{ color: "white" }}>Turboprops are the lever to unlock regional connectivity by air:</strong>
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[
                <>
                  <strong style={{ color: "white" }}>45% lower fuel burn and emissions</strong> than comparable
                  regional jets
                </>,
                <>
                  Superior economics on <strong style={{ color: "white" }}>short-haul sectors</strong>, which dominate
                  India's mobility landscape
                </>,
                <>
                  <strong style={{ color: "white" }}>420 viable new turboprop routes</strong> identified, serving up to
                  35 million additional passengers
                </>,
                <>
                  Resilience to high fuel prices, which represent up to{" "}
                  <strong style={{ color: "white" }}>40% of Indian carriers' operating costs</strong>
                </>,
              ].map((content, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    gap: 10,
                    fontSize: 14,
                    lineHeight: 1.6,
                    color: "rgba(255,255,255,0.7)",
                    fontWeight: 300,
                  }}
                >
                  <span style={{ color: "#56C177", fontWeight: 700, flexShrink: 0 }}>→</span>
                  <span>{content}</span>
                </div>
              ))}
            </div>

            {/* Boutons remontés ici, juste sous le texte, au lieu d'être tout en bas de la section */}
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: "auto", paddingTop: 24, width: "100%" }}>
              <a href="https://www.atr-aircraft.com/about-us/turboprop/" target="_blank" rel="noopener noreferrer"
                style={{
                  display: "block",
                  textAlign: "center",
                  padding: "18px 20px",
                  borderRadius: 12,
                  border: "1px solid #009983",
                  color: "#00d5b8",
                  fontWeight: 700,
                  fontSize: 16,
                  textDecoration: "none",
                  background: "rgba(0,153,131,0.06)",
                  cursor: "pointer",
                  boxShadow: "0 0 12px rgba(0,153,131,0.2)",
                  transition: "all 0.25s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.03)";
                  e.currentTarget.style.boxShadow = "0 0 20px rgba(0,153,131,0.45)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow = "0 0 12px rgba(0,153,131,0.2)";
                }}
              >
                Discover the turboprop
              </a>

              <a href="https://www.atr-aircraft.com/" target="_blank" rel="noopener noreferrer"
                style={{
                  display: "block",
                  textAlign: "center",
                  padding: "18px 20px",
                  borderRadius: 12,
                  border: "1px solid #009983",
                  color: "#00d5b8",
                  fontWeight: 700,
                  fontSize: 16,
                  textDecoration: "none",
                  background: "rgba(0,153,131,0.06)",
                  cursor: "pointer",
                  boxShadow: "0 0 12px rgba(0,153,131,0.2)",
                  transition: "all 0.25s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.03)";
                  e.currentTarget.style.boxShadow = "0 0 20px rgba(0,153,131,0.45)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow = "0 0 12px rgba(0,153,131,0.2)";
                }}
              >
                Explore modern aircraft
              </a>

              <a href="https://www.atr-aircraft.com/wp-content/uploads/2023/04/ATR-MobilityMonitor_India-Analysis_digital.pdf" target="_blank" rel="noopener noreferrer"
                style={{
                  display: "block",
                  textAlign: "center",
                  padding: "18px 20px",
                  borderRadius: 12,
                  border: "1px solid #009983",
                  color: "#00d5b8",
                  fontWeight: 700,
                  fontSize: 16,
                  textDecoration: "none",
                  background: "rgba(0,153,131,0.06)",
                  cursor: "pointer",
                  boxShadow: "0 0 12px rgba(0,153,131,0.2)",
                  transition: "all 0.25s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.03)";
                  e.currentTarget.style.boxShadow = "0 0 20px rgba(0,153,131,0.45)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow = "0 0 12px rgba(0,153,131,0.2)";
                }}
              >
                Read more about the Indian story
              </a>
            </div>
          </div>

          {/* Visual column : la carte occupe maintenant tout l'espace disponible et est centrée */}
         {/* Visual column : la carte occupe tout l'espace disponible à droite, sans toucher au composant */}
<div
  className="s1-visual"
  style={{
    display: "flex",
    minWidth: 0,
    height: 720,
    alignItems: "flex-start",   // au lieu de "center"
    justifyContent: "center",
    marginTop: -90,              // ajuste cette valeur selon le besoin
  }}
>
    <div style={{ height: "100%", width: "100%", position: "relative" }}>
      <IndiaMapHero />
    </div>
  </div>
</div>
      </div>
    </Section>
  );
}


/* ============================================================
   ROOT
   ============================================================ */
export default function Story({ navigate }: Props) {
  const [activeStep, setActiveStep] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollToStart = () => {
    if (scrollRef.current) scrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const sections = STEPS.map((s) => document.getElementById(s.id));
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = STEPS.findIndex((s) => s.id === entry.target.id);
            if (idx !== -1) setActiveStep(idx);
          }
        });
      },
      { root: scrollRef.current, threshold: 0.5 }
    );
    sections.forEach((s) => s && obs.observe(s));
    return () => obs.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    el?.scrollIntoView({ behavior: "smooth" });
  };

  const progress = ((activeStep + 1) / STEPS.length) * 100;

  return (
    <div className="story-root">
      <nav className="story-nav">
        <div className="story-nav-header"><div className="story-nav-label">The Story</div></div>
        <div className="story-nav-items">
          {STEPS.map((s, i) => (
            <button key={s.id} className={`story-nav-item ${activeStep === i ? "active" : ""}`}
              style={{ "--item-accent": s.accent } as React.CSSProperties} onClick={() => scrollTo(s.id)}>
              <span className="story-nav-dot" />
              <span className="story-nav-text">
                <span className="story-nav-number">{s.number}</span>
                <span className="story-nav-title">{s.title}</span>
              </span>
            </button>
          ))}
        </div>
        <div className="story-nav-progress">
          <div className="story-nav-progress-track"><div className="story-nav-progress-fill" style={{ width: `${progress}%` }} /></div>
          <div className="story-nav-progress-label">{activeStep + 1} / {STEPS.length}</div>
        </div>
      </nav>

      <div className="story-scroll" ref={scrollRef}>
        <Step1 />
        <Step2 navigate={navigate} />
        <Step3 />
        <Step4 />
        <Step5 />
        <FooterPage onBackToTop={scrollToStart} />
      </div>
    </div>
  );
}